<template>
  <div class="product-card-product-page" :data-productKey="code">
    <div class="product-card-product-page__info-wrap">
      <ProductPhoto
        :product-id="id"
        :title="title"
        :image-url="imageUrl"
        :second-image-url="secondImageUrl"
        class="product-card-product-page__photo"
      />
      <div class="product-card-product-page__info">
        <ProductTitle :title="title" />
        <div class="product-card-product-page__reviews-wrap">
          <div class="product-card-product-page__reviews">
            <StarRating
              v-if="rating"
              :value="rating"
              :disabled="true"
              class="product-card-product-page__rating"
              :title="Number(rating).toFixed(1)"
            />
            <ReviewsCounter :value="reviewsCount" is-product-page />
          </div>
          <ProductCode :code="code" class="product-card-product-page__code" is-adaptive />
        </div>
        <ProductPrice
          v-if="showProductPrice"
          :price="getNewPrice"
          :old-price="getOldPrice"
          class="product-card-product-page__price"
        />
        <span
          v-else-if="!isPreorder"
          class="product-card-product-page__price product-card-product-page__price--not-available"
        >{{ $t('out_of_stock') }}</span
        >
      </div>
    </div>
    <div class="product-card-product-page__button-wrap">
      <div class="product-card-product-page__buy-btn-wrap" v-if="(isAvailable || availableInStore) && !isPreorder">
        <BuyBtn
          v-if="this.priceObject.price_new && (inStock || availableInStore)"
          :title-ru="titleRu"
          :product-id="id"
          :code="code"
          :price="this.priceObject.price_new"
          :store-id="availableInStore ? availableInStore.store_id : ''"
          size="medium"
          product-page
          small-card
          class="product-card-product-page__buy-btn"
          :class="{ 'product-card-product-page__buy-btn--available-in-store': availableInStore }"
        />
        <BuyBtn
          v-if="!isManager && !availableInStore && config.isEnabledOneClickAndCallback"
          type="invert"
          size="medium"
          :product-id="id"
          one-click-btn
          product-page
          small-card
          class="product-card-product-page__one-click-btn"
        />
      </div>
      <BuyBtn
        v-else-if="isPreorder && !isArchive"
        pre-order-btn
        :title-ru="titleRu"
        :product-id="id"
        :code="code"
        :price="this.priceObject.price_new"
        size="medium"
        small-card
        product-page
        class="product-card-product-page__buy-btn"
        :class="{ 'product-card-product-page__buy-btn--novelty': isPreorder }"
      />
      <WaitListBtn
        v-else
        :in-wait-list-status="isInWaitList"
        :title-ru="titleRu"
        :title-analytics="titleAnalytics"
        :product-id="id"
        is-small-card
        small-card
        product-page
        class="wait-list--card"
      />
      <AddToComparisonBtn
        :product-id="id"
        :title-ru="titleRu"
        :title-analytics="titleAnalytics"
        product-page
        class="product-card-product-page__add-to-comparison"
      />
      <WishListBtn
        class="product-card-product-page__wish-list-btn"
        :id="id"
        :price="getNewPrice"
        :analytics-data="analyticsData"
        :code="code"
        :is-in-stock="isAvailable"
        :in-wish-list-status="isInWishList"
        product-page
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ProductPhoto from '../ProductPhoto'
import ProductTitle from '../ProductTitle'
import ProductCode from '../ProductCode'
import ProductPrice from '../ProductPrice'
import StarRating from '../StarRating'
import ReviewsCounter from '../ReviewsCounter'
import BuyBtn from '../../cart/BuyBtn'
import WaitListBtn from '../product-page/WaitListBtn'
import AddToComparisonBtn from '../product-comparison/AddToComparisonBtn'
import WishListBtn from '../WishListBtn'

export default {
  name: 'ProductCardProductPage',
  components: {
    ProductTitle,
    ProductPhoto,
    ProductCode,
    ProductPrice,
    StarRating,
    ReviewsCounter,
    BuyBtn,
    WaitListBtn,
    AddToComparisonBtn,
    WishListBtn
  },
  props: {
    id: {
      type: Number,
      default: null
    },
    analyticsData: {
      type: Array
    },
    code: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    titleRu: {
      type: String,
      default: ''
    },
    titleAnalytics: {
      type: String,
      default: ''
    },
    imageUrl: {
      type: String,
      required: false,
      default: ''
    },
    secondImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    rating: {
      type: String,
      default: ''
    },
    reviewsCount: {
      type: Number,
      default: null
    },
    price: {
      type: String,
      default: ''
    },
    isAvailable: {
      type: Boolean,
      default: false
    },
    isNovelty: {
      type: Boolean,
      defaultL: false
    },
    isPreorder: {
      type: Boolean,
      default: false
    },
    isArchive: {
      type: Boolean,
      default: false
    },
    isInWaitList: {
      type: Boolean,
      default: false
    },
    isInWishList: {
      type: Boolean,
      default: false
    },
    availableInStore: [Boolean, Object],
    inStock: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters(['isManager']),
    ...mapState({
      config: state => state.init.config
    }),
    parentCategoryCode () {
      if (this.analyticsData.length) {
        const mainProduct = this.analyticsData.find(el => el.is_main_product)
        if (mainProduct) {
          return mainProduct.id
        }
        return this.analyticsData[0].id
      }
      return this.code
    },
    priceObject: function () {
      return JSON.parse(this.price)
    },
    getNewPrice () {
      return +this.priceObject.price_new ?? null
    },
    getOldPrice () {
      return this.priceObject.price_old && this.priceObject.price_old !== this.priceObject.price_new
        ? +this.priceObject.price_old
        : null
    },
    showProductPrice () {
      return (this.isAvailable && this.getNewPrice) || (this.availableInStore && this.getNewPrice) || (this.isPreorder && this.getNewPrice)
    }
  }
}
</script>

<style scoped lang="sass">

.product-card-product-page
  position: relative
  padding: 20px
  border: 1px solid $nd-border-color
  border-radius: 3px

.product-card-product-page__info-wrap
  display: flex
  flex-direction: row
  margin-bottom: 40px

.product-card-product-page__photo
  max-width: 120px

.product-card-product-page__info
  width: 100%
  margin-left: 20px

.product-card-product-page__reviews-wrap,
.product-card-product-page__reviews
  display: flex
  align-items: center

.product-card-product-page__reviews-wrap
  flex-flow: row wrap
  justify-content: space-between

.product-card-product-page__reviews-wrap
  margin-bottom: 30px

.product-card-product-page__reviews
  align-items: baseline

.product-card-product-page__rating
  margin-right: 10px

.product-card-product-page__price
  +mmax(1366)
    display: flex
    flex-direction: column-reverse

.product-card-product-page__price--not-available
  color: $placeholder-color
  font-weight: 500
  font-size: 16px
  line-height: 19px

  +mmin($tablet)
    font-size: 18px
    line-height: 21px

.product-card-product-page__button-wrap
  display: flex
  flex-flow: row wrap
  align-items: center
  margin-top: auto
  justify-content: space-between
  position: relative

.product-card-product-page__buy-btn-wrap
  display: flex
  justify-content: space-between
  flex-flow: row wrap
  width: 100%

.product-card-product-page__buy-btn
  width: 48%

  +mmax(1366)
    width: 100%

  &--novelty
    width: 100%

  &--available-in-store
    width: 100%

.wait-list--card
  width: 100%

.product-card-product-page__one-click-btn
  width: 48%

  +mmax(1366)
    margin-top: 20px
    width: 100%

.wish-list-btn__text
  display: block

.product-card-product-page__price
  margin: 30px 0 0

.product-card-product-page__add-to-comparison,
.product-card-product-page__wish-list-btn
  margin-top: 20px
  width: 48%

  +mmax(1436)
    width: 100%
    margin-right: 0

.product-card-product-page__wish-list-btn
  margin-left: 0
</style>
